'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { authService } from '@/lib/authService';
import { normalizeOperationsTeamRole } from '@/lib/operations/teamConfig';
import { useOperationsUsers } from '@/hooks/useOperationsUsers';
import { useContractors, type Contractor } from '@/hooks/useContractors';
import { isSilentFetchError } from '@/lib/capacitorFetchPatch';

export interface OperationsAssignmentUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  module_role?: string;
  status?: string;
  is_field_worker?: boolean;
  avatar_url?: string | null;
}

export interface OperationsAssignmentTeamMember {
  id: string;
  team_id: string;
  user_id: number;
  employee_id?: number | null;
  employee_number?: string | null;
  role: string;
  normalized_role: 'user' | 'supervisor' | 'admin';
  first_name: string;
  last_name: string;
  email: string;
  avatar_url?: string | null;
  is_active: boolean;
  has_total_operations_access?: boolean;
  has_total_attendance_access?: boolean;
  isLeader: boolean;
  name: string;
}

export interface OperationsAssignmentTeam {
  id: string;
  team_name: string;
  description?: string;
  color?: string;
  icon?: string;
  team_type: string;
  members: OperationsAssignmentTeamMember[];
  memberUserIds: number[];
  leader: OperationsAssignmentTeamMember | null;
}

interface RawTeam {
  id: string;
  team_name: string;
  description?: string;
  color?: string;
  icon?: string;
  team_type: string;
  is_archived?: boolean;
  settings?: Record<string, unknown> | null;
  solution_key?: string | null;
}

interface RawTeamMemberResponse {
  id: string;
  team_id: string;
  user_id: number;
  employee_id?: number | null;
  employee_number?: string | null;
  role: string;
  module_access?: {
    total_operations?: boolean;
    total_attendance?: boolean;
  };
  user?: {
    id: number;
    email: string;
    first_name?: string;
    last_name?: string;
    avatar_url?: string | null;
    is_active?: boolean;
  };
}

export interface OperationsAssignmentContractor extends Contractor {
  assignee_type: 'contractor';
}

export interface UseOperationsAssignmentOptionsOptions {
  moduleKey?: string;
  activeSolutionKey?: string | null;
}

interface UseOperationsAssignmentOptionsReturn {
  users: OperationsAssignmentUser[];
  teams: OperationsAssignmentTeam[];
  contractors: Contractor[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  getUserName: (userId: number) => string;
  getUserById: (userId: number) => OperationsAssignmentUser | undefined;
  getTeamById: (teamId?: string | null) => OperationsAssignmentTeam | undefined;
  getTeamMemberUserIds: () => number[];
  getContractorName: (contactId: number) => string;
  getContractorById: (contactId: number) => Contractor | undefined;
}

const getMemberDisplayName = (member: {
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
}) => {
  const fullName = [member.first_name, member.last_name]
    .filter((part): part is string => typeof part === 'string' && part.trim().length > 0)
    .join(' ')
    .trim();

  return fullName || member.email || 'Unnamed user';
};

export function useOperationsAssignmentOptions(options?: UseOperationsAssignmentOptionsOptions): UseOperationsAssignmentOptionsReturn {
  const resolvedModuleKey = options?.moduleKey || 'total-operations';
  const activeSolutionKey = options?.activeSolutionKey || null;
  const {
    users,
    loading: loadingUsers,
    error: usersError,
    getUserName,
    refreshUsers,
  } = useOperationsUsers({ moduleKey: resolvedModuleKey });
  const {
    contractors,
    loading: loadingContractors,
    error: contractorsError,
    getContractorName,
    refreshContractors,
  } = useContractors();
  const [teams, setTeams] = useState<OperationsAssignmentTeam[]>([]);
  const [loadingTeams, setLoadingTeams] = useState(true);
  const [teamsError, setTeamsError] = useState<string | null>(null);

  const loadTeams = useCallback(async () => {
    try {
      setLoadingTeams(true);
      setTeamsError(null);

      const token = authService.getTokens()?.access_token;
      if (!token) {
        throw new Error('No authentication token');
      }

      const teamsResponse = await fetch('/api/v1/teams?scope=mine', {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!teamsResponse.ok) {
        throw new Error(`Failed to load teams: ${teamsResponse.statusText}`);
      }

      const teamsData = await teamsResponse.json();
      if (!teamsData.success) {
        throw new Error(teamsData.error || 'Failed to load teams');
      }

      const allRawTeams: RawTeam[] = (teamsData.teams || teamsData.data || [])
        .filter((team: RawTeam) => !team.is_archived);

      const rawTeams = resolvedModuleKey === 'total-operations'
        ? allRawTeams.filter((team: RawTeam) => {
            // Include both operations_team and solution types for total-operations
            return team.team_type === 'operations_team' || team.team_type === 'solution';
          })
        : allRawTeams.filter((team: RawTeam) => {
            const settings = team.settings && typeof team.settings === 'object' ? team.settings : {};
            const scopes: string[] = Array.isArray((settings as any).module_scopes)
              ? (settings as any).module_scopes
              : typeof (settings as any).module_scope === 'string'
                ? [(settings as any).module_scope]
                : [];
            return scopes.length === 0 || scopes.some((s) => s === resolvedModuleKey || s === 'total-operations');
          });

      // Filter teams by solution_key when activeSolutionKey is provided
      const solutionFilteredTeams = activeSolutionKey
        ? rawTeams.filter((team: RawTeam) => {
            // solution_key is stored directly on the team record, not in settings
            return team.solution_key === activeSolutionKey;
          })
        : rawTeams;

      const teamResults = await Promise.all(solutionFilteredTeams.map(async (team) => {
        try {
          const membersResponse = await fetch(`/api/v1/teams/${team.id}/members`, {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });

          if (!membersResponse.ok) {
            console.warn(`Skipping team member load for ${team.team_name || team.id}: ${membersResponse.status}`);
            return null;
          }

          const membersData = await membersResponse.json();
          if (!membersData.success) {
            console.warn(membersData.error || `Failed to load members for ${team.team_name || team.id}`);
            return null;
          }

          const members = ((membersData.data || []) as RawTeamMemberResponse[])
            .filter((member) => member.user && member.user.id != null)
            .map<OperationsAssignmentTeamMember>((member) => {
              const normalizedRole = normalizeOperationsTeamRole(member.role, team.settings || undefined);
              const firstName = member.user?.first_name || '';
              const lastName = member.user?.last_name || '';
              const email = member.user?.email || '';

              return {
                id: member.id,
                team_id: member.team_id,
                user_id: member.user!.id,
                employee_id: member.employee_id != null ? Number(member.employee_id) : null,
                employee_number: member.employee_number || null,
                role: member.role,
                normalized_role: normalizedRole,
                first_name: firstName,
                last_name: lastName,
                email,
                avatar_url: member.user?.avatar_url || null,
                is_active: Boolean(member.user?.is_active ?? true),
                has_total_operations_access: Boolean(member.module_access?.total_operations),
                has_total_attendance_access: Boolean(member.module_access?.total_attendance),
                isLeader: normalizedRole === 'supervisor',
                name: getMemberDisplayName({ first_name: firstName, last_name: lastName, email }),
              };
            });

          const leader = members.find((member) => member.isLeader) || members[0] || null;

          return {
            id: team.id,
            team_name: team.team_name,
            description: team.description,
            color: team.color,
            icon: team.icon,
            team_type: team.team_type,
            members,
            memberUserIds: members.map((member) => member.user_id),
            leader,
          } satisfies OperationsAssignmentTeam;
        } catch (teamError) {
          if (!isSilentFetchError(teamError)) console.warn(`Skipping team member load for ${team.team_name || team.id}:`, teamError);
          return null;
        }
      }));

      const loadedTeams = teamResults.reduce<OperationsAssignmentTeam[]>((acc, team) => {
        if (team) {
          acc.push(team);
        }
        return acc;
      }, []);

      setTeams(loadedTeams);

      // When filtering by solution, also filter users to only include team members
      if (activeSolutionKey && loadedTeams.length > 0) {
        const teamMemberUserIds = new Set<number>();
        loadedTeams.forEach((team) => {
          team.memberUserIds.forEach((userId) => teamMemberUserIds.add(Number(userId)));
        });
        // Note: We don't filter the users array here since it comes from useOperationsUsers
        // Instead, consumers should check team membership when displaying users
      }
    } catch (error: any) {
      if (!isSilentFetchError(error)) console.error('Error loading operations assignment teams:', error);
      setTeamsError(error?.message || 'Failed to load assignment teams');
      setTeams([]);
    } finally {
      setLoadingTeams(false);
    }
  }, [resolvedModuleKey, activeSolutionKey]);

  useEffect(() => {
    loadTeams();
  }, [loadTeams]);

  const refresh = useCallback(async () => {
    await Promise.all([refreshUsers(), loadTeams(), refreshContractors()]);
  }, [loadTeams, refreshUsers, refreshContractors]);

  const userLookup = useMemo(() => {
    const lookup = new Map<number, OperationsAssignmentUser>();
    users.forEach((user) => lookup.set(user.id, user));
    return lookup;
  }, [users]);

  const teamLookup = useMemo(() => {
    const lookup = new Map<string, OperationsAssignmentTeam>();
    teams.forEach((team) => lookup.set(team.id, team));
    return lookup;
  }, [teams]);

  const contractorLookup = useMemo(() => {
    const lookup = new Map<number, Contractor>();
    contractors.forEach((c) => lookup.set(c.id, c));
    return lookup;
  }, [contractors]);

  const getTeamMemberUserIds = useCallback(() => {
    const memberIds = new Set<number>();
    teams.forEach((team) => {
      team.memberUserIds.forEach((userId) => memberIds.add(Number(userId)));
    });
    return Array.from(memberIds);
  }, [teams]);

  return {
    users,
    teams,
    contractors,
    loading: loadingUsers || loadingTeams || loadingContractors,
    error: usersError || teamsError || contractorsError,
    refresh,
    getUserName,
    getUserById: (userId: number) => userLookup.get(userId),
    getTeamById: (teamId?: string | null) => (teamId ? teamLookup.get(teamId) : undefined),
    getTeamMemberUserIds,
    getContractorName,
    getContractorById: (contactId: number) => contractorLookup.get(contactId),
  };
}
